import gql from 'graphql-tag';
import { snappy } from 'src/_shared/services/utils';

export const GET_EMPLOYEE_BADGE_ASSIGNMENTS = gql`
	query QueryBadgeAssignments($companyId: ID!, $userId: ID, $limit: Int) {
		queryBadgeAssignments(
			companyId: $companyId
			userId: $userId
			limit: $limit
		) {
			items {
				approvalStatus
				awardedBy
				id
				badgeId
				badge {
					id
					name
					snappyGrantId
					snappyGrant {
						id
						name
						externalSnappyId
						snappyCampaignTierId
						costInCurrency
					}
				}
				rewardBonus
				companyId
				userGroupId
				userId
				createdAt
				updatedAt
				user {
					id
					firstName
					lastName
					emailAddress
				}
			}
			nextToken
		}
	}
`;

export const CREATE_SNAPPY_GIFT = gql`
	mutation CreateSnappyGift($input: SnappyGiftInput!) {
		createSnappyGift(input: $input) {
			id
			companyId
			userId
			snappyCampaignTierId
			snappyGrantId
			snappyCampaignId
			externalSnappyGiftId
			link
			claimedOn
		}
	}
`;

export const UPDATE_SNAPPY_GIFT = gql`
	mutation UpdateSnappyGift($input: SnappyGiftInput!) {
		updateSnappyGift(input: $input) {
			id
			companyId
			userId
			snappyCampaignTierId
			snappyGrantId
			snappyCampaignId
			externalSnappyGiftId
			link
			claimedOn
		}
	}
`;

export const QUERY_SNAPPY_GIFTS_BY_USER_ID_COMPANY_ID = gql`
	query QuerySnappyGiftsByUserIdCompanyId($userId: ID!, $companyId: ID!) {
		querySnappyGiftsByUserIdCompanyId(userId: $userId, companyId: $companyId) {
			id
			companyId
			itemType
			userId
			badgeAssignmentId
			badgeAssignment {
				id
				badgeId
				badge {
					name
					id
				}
			}
			link
			snappyCampaignTierId
			snappyGrantId
			snappyCampaignId
			externalSnappyGiftId
			firstSeenAt
			createdAt
			claimedOn
		}
	}
`;

export class SnappyGiftService {
	constructor(apolloClient) {
		this.apolloClient = apolloClient;
	}

	async getEmployeeBadgeAssignments(companyId, userId, limit = 20) {
		try {
			const { data } = await this.apolloClient.query({
				query: GET_EMPLOYEE_BADGE_ASSIGNMENTS,
				variables: { companyId, userId, limit },
				fetchPolicy: 'network-only',
			});
			return data?.queryBadgeAssignments?.items || [];
		} catch (error) {
			console.error('Error fetching employee badge assignments:', error);
			return [];
		}
	}

	async createSnappyGiftRecord(input) {
		try {
			const { data } = await this.apolloClient.mutate({
				mutation: CREATE_SNAPPY_GIFT,
				variables: { input },
			});
			return data?.createSnappyGift;
		} catch (error) {
			console.error('Error creating snappy gift record:', error);
			throw error;
		}
	}

	async updateSnappyGift(input) {
		try {
			const { data } = await this.apolloClient.mutate({
				mutation: UPDATE_SNAPPY_GIFT,
				variables: { input },
			});
			return data?.updateSnappyGift;
		} catch (error) {
			console.error('Error updating snappy gift:', error);
			throw error;
		}
	}
	getSnappyGift = async (userId, externalSnappyGiftId) => {
		try {
			const response = await snappy({
				type: 'getSnappyGiftById',
				userId: userId,
				id: externalSnappyGiftId,
			});
			return response;
		} catch (error) {
			console.error('Error getting snappy gift details:', error);
			return null;
		}
	};

	async querySnappyGiftsByUserIdCompanyId(userId, companyId) {
		try {
			const { data } = await this.apolloClient.query({
				query: QUERY_SNAPPY_GIFTS_BY_USER_ID_COMPANY_ID,
				variables: { userId, companyId },
				fetchPolicy: 'network-only',
			});

			const gifts = data?.querySnappyGiftsByUserIdCompanyId || [];
			return gifts;
		} catch (error) {
			console.error('Error querying snappy gifts by user and company:', error);
			return [];
		}
	}

	async ensureSnappyGiftExists(userId, companyId, snappyGrantId) {
		try {
			const existingGifts = await this.querySnappyGiftsByUserIdCompanyId(
				userId,
				companyId
			);
			const existingGift = existingGifts.find(
				(gift) => gift.snappyGrantId === snappyGrantId
			);

			if (existingGift) {
				return existingGift;
			}

			const now = new Date().toISOString();
			const newGiftInput = {
				userId,
				companyId,
				snappyGrantId,
				externalSnappyGiftId: 'undefined',
			};

			return await this.createSnappyGiftRecord(newGiftInput);
		} catch (error) {
			console.error('Error ensuring snappy gift exists:', error);
			throw error;
		}
	}

	async markGiftAsSeen(userId, companyId, snappyGrantId) {
		try {
			const gift = await this.ensureSnappyGiftExists(
				userId,
				companyId,
				snappyGrantId
			);

			if (gift.firstSeenAt) {
				return gift;
			}

			const updateInput = {
				id: gift.id,
				userId: userId,
				companyId: companyId,
				snappyGrantId: snappyGrantId,
			};

			return await this.updateSnappyGift(updateInput);
		} catch (error) {
			console.error('Error marking gift as seen:', error);
			throw error;
		}
	}

	async checkForNewGifts(userId, companyId, badgeAssignments) {
		try {
			const newGifts = [];
			const firstTimeGifts = [];
			const subsequentGifts = [];

			for (const assignment of badgeAssignments) {
				if (
					assignment.approvalStatus === 'Approved' &&
					assignment.badge?.snappyGrantId
				) {
					const existingGifts = await this.querySnappyGiftsByUserIdCompanyId(
						userId,
						companyId
					);
					const existingGift = existingGifts.find(
						(gift) => gift.snappyGrantId === assignment.badge.snappyGrantId
					);

					if (!existingGift || !existingGift.externalSnappyGiftId) {
						const giftInfo = {
							snappyGrantId: assignment.badge.snappyGrantId,
							badgeName: assignment.badge.name,
							campaignTierId: assignment.badge.snappyGrant?.id,
							isFirstTime: !existingGift,
							hasBeenSeen: existingGift?.firstSeenAt !== null,
							assignment,
						};

						if (!existingGift) {
							await this.ensureSnappyGiftExists(
								userId,
								companyId,
								assignment.badge.snappyGrantId
							);
							firstTimeGifts.push(giftInfo);
						} else if (!existingGift.firstSeenAt) {
							// Gift exists but hasn't been seen yet - still first time
							firstTimeGifts.push(giftInfo);
						} else {
							// Gift has been seen but not claimed - subsequent view
							subsequentGifts.push(giftInfo);
						}

						newGifts.push(giftInfo);
					}
				}
			}

			return {
				allGifts: newGifts,
				firstTimeGifts,
				subsequentGifts,
			};
		} catch (error) {
			console.error('Error checking for new gifts:', error);
			return {
				allGifts: [],
				firstTimeGifts: [],
				subsequentGifts: [],
			};
		}
	}

	filterBudgetInformation(activities) {
		return activities.map((activity) => {
			const filtered = { ...activity };

			if (filtered.rawData) {
				const {
					costInCurrency,
					remainingBudget,
					budgetAfterTotal,
					budgetBeforeTotal,
					recognitionAmount,
					...safeRawData
				} = filtered.rawData;

				if (safeRawData.user) {
					const { recognitionEvents, ...safeUser } = safeRawData.user;
					safeRawData.user = safeUser;
				}

				filtered.rawData = safeRawData;
			}

			return filtered;
		});
	}

	processRecentActivities(badgeGiftsWithStatus = []) {
		const activities = [];

		// Process badge gifts with status data for non-tier cards
		badgeGiftsWithStatus.forEach((gift) => {
			if (!gift.statusData) {
				return; // Skip gifts without status data
			}

			const status = gift.statusData.status;

			// Don't show cards with "claimed" status
			if (status === 'claimed') {
				return;
			}

			const badgeName = gift.badgeAssignment?.badge?.name || 'Recognition';

			const giftActivity = {
				id: `gift-${gift.id}`,
				type: 'gift_received',
				badgeName: badgeName,
				snappyGrantId: gift.snappyGrantId,
				createdAt: gift.createdAt,
				isNew: this.isNewActivity(gift.createdAt),
				status: status === 'unopened' ? 'pending' : 'unwrapped',
				hasSeen: false,
				giftStatus: status,
				link: gift.link,
				rawData: {
					statusData: gift.statusData,
					gift: gift,
				},
			};
			activities.push(giftActivity);
		});

		activities.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

		return this.filterBudgetInformation(activities.slice(0, 10));
	}

	isNewActivity(timestamp) {
		const now = new Date();
		const activityTime = new Date(timestamp);
		const diffInHours = (now - activityTime) / (1000 * 60 * 60);
		return diffInHours <= 24;
	}
}

export default SnappyGiftService;
